import {
  AppContext,
  assets,
  ErrorHandling,
  HightLightTable,
  HightLightTableColum,
  Permission,
  SelectSearch,
  sharedStyle
} from '@pulseops/common'
import { OPLSharedStyles } from '@pulseops/submission'
import { SectionCol, SectionContent, SectionRow } from '@pulseops/submission/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native'
import { ACSharedStyles } from '../../admin-config-common'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { pipe } from 'fp-ts/lib/function'
import { ACViewMetricListService } from '../../admin-config-service'
import { ZIO } from '@mxt/zio'
import { Controller, useForm } from 'react-hook-form'
import { ACViewMetricListForm } from './ACViewMetricListForm'
import { useLoading } from '@mxt/zio-react'
import { makeStyles } from '@material-ui/core'
import { StackScreenProps } from '@react-navigation/stack'
import { AdministratorConfigStackParamList } from '../../AdministrationConfigStackParamList'
import { AdministrationConfigContext } from '../../AdministrationConfigContext'
export const ACViewMetricList = (props: StackScreenProps<AdministratorConfigStackParamList>) => {
  const { t } = useTranslation()
  const { changeBreadcrumb } = React.useContext(AppContext.AppContextInstance)
  const isFocused = useIsFocused()
  const rootNavigation = useNavigation()
  const { height, width } = useWindowDimensions()
  const [isLoading, bindLoader] = useLoading(false)
  const { showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const { userPermissions } = React.useContext(AdministrationConfigContext)
  const [searchedMetricList, setSearchedMetricList] = React.useState<ACViewMetricListService.ACViewMetricResponse[]>([])
  const [metricGroups, setMetricGroups] = React.useState<ACViewMetricListService.ACMetricGroupInfo[]>([])
  const { control, getValues, watch } = useForm<ACViewMetricListForm.ACViewMetricSearchedListInfo>({
    defaultValues: {
      groupMetric: undefined
    }
  })

  const makeTableStyles = makeStyles({
    container: {
      border: `1px solid #D3DCE6`,
      borderWidth: 1,
      borderRadius: 8,
      maxHeight: height - 343,
      maxWidth: width - 40
    }
  })

  const displayedColumns: HightLightTableColum[] = [
    {
      title: t('AdministratorConfig:GroupMetrics'),
      fieldName: 'groupMetrics'
    },
    {
      title: t('AdministratorConfig:MetricName'),
      fieldName: 'metricName'
    },
    {
      title: t('AdministratorConfig:Value'),
      fieldName: 'value'
    },
    {
      title: t('AdministratorConfig:Description'),
      fieldName: 'description'
    },
    {
      title: t('AdministratorConfig:Operator'),
      fieldName: 'operator',
      disabled: true
    },
    {
      title: t('AdministratorConfig:OperatorValue'),
      fieldName: 'operatorValue',
      disabled: true
    },
    {
      title: t('AdministratorConfig:RAGScore'),
      fieldName: 'ragThreshold',
      disabled: true
    },
    {
      title: t('AdministratorConfig:Multiplier'),
      fieldName: 'multiplier',
      disabled: true
    },
    {
      title: t('AdministratorConfig:Action'),
      fieldName: '',
      disabled: true,
      rowRender: (selectedProps) => {
        const groupMetrics = selectedProps.selectItem['groupId'].toString()
        return (
          <View style={ACSharedStyles.buttonContainer}>
            {userPermissions.includes(Permission.EditMetricListBSCAdminConfig) && (
              <TouchableOpacity
                style={[sharedStyle.button, sharedStyle.btnRed, OPLSharedStyles.buttonContent]}
                onPress={() => onEditEvent(groupMetrics)}
              >
                <Text style={sharedStyle.textButtonRed}>{t('AdministratorConfig:Edit')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )
      }
    }
  ]

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            rootNavigation.navigate('HomeScreen')
          }
        },
        {
          title: t('AdministratorConfig:MetricsConfiguration'),
          navigate: null
        }
      ])
      if (userPermissions && userPermissions.length > 0) {
        if (!userPermissions.includes(Permission.LinkMetricListBSCAdminConfig)) {
          rootNavigation.navigate('HomeScreen')
        } else {
          getMetricGroups()
          getViewMetricList()
        }
      }
    }

    return () => {
      setSearchedMetricList([])
    }
  }, [isFocused, userPermissions])

  React.useEffect(() => {
    showGlobalLoading(isLoading)
  }, [isLoading])

  const getMetricGroups = () => {
    pipe(
      ACViewMetricListService.getACMetricGroupList,
      ZIO.map((response) => {
        if (response && response.length > 0) {
          setMetricGroups(response)
        } else {
          setMetricGroups([])
        }
        return response
      }),
      ZIO.unsafeRun({})
    )
  }

  const getViewMetricList = () => {
    const formData = getValues()
    const bodyData: ACViewMetricListService.ACViewMetricListRequest = {
      start: 0,
      size: 100,
      orders: [],
      sort: null,
      groupId: formData.groupMetric?.value || '',
      metricCode: null,
      visible: true,
      isPaginate: false
    }
    pipe(
      ACViewMetricListService.getACViewMetricList(bodyData),
      ZIO.map((metricResponse) => {
        if (metricResponse.data && metricResponse.data.length > 0) {
          setSearchedMetricList(metricResponse.data)
        } else {
          setSearchedMetricList([])
        }
        return metricResponse
      }),
      ZIO.mapError((error) => {
        setSearchedMetricList([])
        return error
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const onSearchEvent = () => {
    getViewMetricList()
  }

  const onEditEvent = (groupId: string) => {
    if (userPermissions.includes(Permission.ViewEditMetricBSCAdminConfig)) {
      props.navigation.navigate('ACEditMetricList', {
        groupId: groupId
      })
    }
  }

  return (
    <View style={ACSharedStyles.wrapper}>
      <ScrollView style={OPLSharedStyles.container} horizontal={false}>
        <View style={ACSharedStyles.headingContainer}>
          <Text style={ACSharedStyles.headingText}>{t('AdministratorConfig:ConfigMetricsforAgentScorecard')}</Text>
        </View>
        <SectionContent>
          <SectionRow>
            <SectionCol>
              <Controller
                control={control}
                name={'groupMetric'}
                render={({ field: { onChange, onBlur, value }, fieldState }) => (
                  <SelectSearch
                    label={t('AdministratorConfig:GroupMetrics')}
                    options={metricGroups.map((item) => ({
                      value: item.groupId,
                      label: `${item.groupId} - ${item.groupMetrics}`
                    }))}
                    value={value}
                    onBlur={onBlur}
                    onChange={(val) => {
                      onChange(val)
                    }}
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    placeholder={t('AdministratorConfig:Select')}
                  />
                )}
              />
            </SectionCol>
          </SectionRow>
          {userPermissions.includes(Permission.SearchMetricListBSCAdminConfig) && (
            <View style={[OPLSharedStyles.buttonContainer, OPLSharedStyles.secondLine]}>
              <TouchableOpacity
                style={[sharedStyle.button, sharedStyle.btnRed, OPLSharedStyles.buttonContent]}
                onPress={() => onSearchEvent()}
              >
                <Text style={sharedStyle.textButtonRed}>{t('AdministratorConfig:Search')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </SectionContent>
        <HightLightTable
          displayedColumns={displayedColumns}
          dataSource={searchedMetricList}
          tableContainerClassName={makeTableStyles().container}
          stickyHeader
        ></HightLightTable>
      </ScrollView>
    </View>
  )
}
